import { useNavigate } from 'react-router-dom';

const DAY_MS = 24 * 60 * 60 * 1000;

/** `14 FEB` — evidence-line date. */
const monoDay = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }).toUpperCase();
};

const daysBetween = (a, b) => Math.max(0, Math.round((b - a) / DAY_MS));

const TONE_TEXT = {
  needs: 'text-amber-ink',
  breach: 'text-breach-ink',
  done: 'text-brand-ink',
};

const StatTile = ({ as: As = 'div', label, value, hint, tone, children, testId, ...rest }) => (
  <As
    className={`rounded-lg bg-ground p-4 sm:p-5 flex flex-col gap-1 ${As !== 'div' ? 'text-left hover:bg-inset transition-colors duration-state' : ''}`}
    data-testid={testId}
    {...rest}
  >
    <span className="pd-eyebrow">{label}</span>
    <span className={`font-heading text-[36px] sm:text-[42px] font-bold tracking-[-0.035em] leading-none ${TONE_TEXT[tone] || 'text-ink'}`}>
      {value}
    </span>
    {hint && <span className="text-sm text-ink-55">{hint}</span>}
    {children}
  </As>
);

/**
 * SARStats — four numbers a person actually wants at a glance: how long
 * this has been going, whose clock it is and how much of it is left (or how
 * long they took, once records are back), whether anything has come back,
 * and how much correspondence there is to read if they want to. This is
 * the first thing on the page after the headline — reading the thread
 * itself is secondary to knowing where things stand.
 */
const SARStats = ({ sar, deadline, emailThread }) => {
  const navigate = useNavigate();
  const status = sar?.effective_status || sar?.status;
  if (!sar?.sent_at || status === 'draft') return null;

  const sentDate = new Date(sar.sent_at);
  const daysSinceSent = daysBetween(sentDate, new Date());

  const isComplete = status === 'complete';
  const daysRemaining = typeof sar.days_remaining === 'number' ? sar.days_remaining : null;
  const overdue = !isComplete && daysRemaining !== null && daysRemaining < 0;
  const dueDate = deadline?.extended_deadline || deadline?.initial_deadline;
  const dueDateObj = dueDate ? new Date(dueDate) : null;

  // The bar inside the deadline tile: same elapsed/overrun idea as the old
  // full-width tracker, just compact — a glance at how far along the clock
  // is, not a second reading of the number above it.
  const bar = !isComplete && dueDateObj && (() => {
    const now = new Date();
    const span = Math.max((overdue ? now : dueDateObj) - sentDate, DAY_MS);
    const clamp = (n) => Math.min(100, Math.max(0, n));
    const elapsedPct = clamp(((Math.min(now, dueDateObj) - sentDate) / span) * 100);
    const overrunPct = overdue ? clamp(((now - dueDateObj) / span) * 100) : 0;
    return (
      <div className="mt-2 relative h-[4px] w-full overflow-hidden rounded-full bg-inset" aria-hidden="true">
        <div className="absolute inset-y-0 left-0 bg-amber" style={{ width: `${elapsedPct}%` }} />
        {overrunPct > 0 && (
          <div className="absolute inset-y-0 bg-breach" style={{ left: `${elapsedPct}%`, width: `${overrunPct}%` }} />
        )}
      </div>
    );
  })();

  const docCount = sar.document_count || 0;
  const replyCount = emailThread?.length || 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="sar-stats">
      <StatTile
        label="Sent"
        value={daysSinceSent}
        hint={`day${daysSinceSent === 1 ? '' : 's'} ago · ${monoDay(sentDate)}`}
        testId="sar-stat-sent"
      />

      {isComplete ? (
        <StatTile
          label="They took"
          value={sar.response_received_at ? daysBetween(sentDate, new Date(sar.response_received_at)) : '—'}
          hint={sar.response_received_at ? `days · arrived ${monoDay(sar.response_received_at)}` : 'Arrived'}
          tone="done"
          testId="sar-stat-deadline"
        />
      ) : (
        <StatTile
          label={overdue ? 'Overdue' : 'Deadline'}
          value={daysRemaining === null ? '—' : Math.abs(daysRemaining)}
          hint={
            daysRemaining === null
              ? 'No deadline yet'
              : overdue
                ? `day${Math.abs(daysRemaining) === 1 ? '' : 's'} past · was due ${monoDay(dueDate)}`
                : `day${daysRemaining === 1 ? '' : 's'} left · due ${monoDay(dueDate)}`
          }
          tone={overdue ? 'breach' : daysRemaining !== null && daysRemaining <= 7 ? 'needs' : undefined}
          testId="sar-stat-deadline"
        >
          {bar}
        </StatTile>
      )}

      <StatTile
        as={docCount ? 'button' : 'div'}
        type={docCount ? 'button' : undefined}
        onClick={docCount ? () => navigate(`/documents?org=${sar.organisation_id}`) : undefined}
        label="Documents"
        value={docCount}
        hint={docCount ? 'See the files →' : 'Nothing back yet'}
        testId="sar-stat-documents"
      />

      <StatTile
        label="Correspondence"
        value={replyCount}
        hint={`message${replyCount === 1 ? '' : 's'} · sent and received`}
        testId="sar-stat-correspondence"
      />
    </div>
  );
};

export default SARStats;
